import { ComponentProps } from "react"
import {
  Text,
  chakra,
  Link,
  Flex,
  Box,
  Button,
  Collapse
} from "@chakra-ui/react"
import { MouseEventHandler, ReactNode, useMemo, useState } from "react"
import DefaultFormWrap, { DefaultLandscapeFormWrap } from "./DefaultAuthScreen"
import { AuthPageStyling } from "src/context/types"
import useSearchParams from "src/hooks/useSearchParams"
import { FaBars } from "react-icons/fa6"
import { ConditionalAppLogo } from "./ConditionalAppLogo"

export const X121Footer = (
  props: ComponentProps<(typeof chakra)["footer"]>
) => {
  const year = useMemo(() => {
    const date = new Date()
    return date.getFullYear()
  }, [])

  return (
    <chakra.footer
      bottom={0}
      left={0}
      width="100%"
      bg="black"
      borderTop="1px solid white"
      padding="10px 10px 8px 10px"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      {...props}
    >
      <Text
        display="flex"
        alignItems="center"
        fontFamily="roboto"
        fontWeight={400}
        color="white"
        fontSize="12px"
        lineHeight={1.5}
      >
        ©&nbsp;{year}&nbsp;x121
      </Text>
      <Flex gap={"3px"}>
        <Link href="https://x121.ai/privacy">
          <Text
            color="white"
            fontSize="11px"
            fontFamily="Roboto"
            fontWeight="700"
          >
            Privacy
          </Text>
        </Link>
        <Text
          color="white"
          fontSize="11px"
          fontFamily="Roboto"
          fontWeight="700"
        >
          |
        </Text>
        <Link href="https://x121.ai/terms">
          <Text
            color="white"
            fontSize="11px"
            fontFamily="Roboto"
            fontWeight="700"
          >
            Terms
          </Text>
        </Link>
      </Flex>
    </chakra.footer>
  )
}

const MobMenu = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <Box
      display="flex"
      pos="absolute"
      top="50px"
      flexDir="column"
      width="100%"
      px="9px"
      boxShadow="rgba(255, 255, 255, 0.1) 0px 1px 0px 0px inset"
      background="black"
    >
      <Collapse in={isOpen}>
        <Link
          href="https://x121.ai/FAQs"
          color="white"
          fontFamily="Roboto"
          fontWeight={400}
          fontSize="16px"
          lineHeight="52px"
          mt="1px"
          textDecor="none"
          _active={{}}
          _hover={{}}
          _focus={{}}
        >
          FAQs
        </Link>
        <Link
          href="#"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgColor="#FFC0CB"
          fontWeight="700"
          fontSize="14px"
          fontFamily="Roboto"
          width="100%"
          height="51px"
          px="11px"
          mb="14px"
          borderRadius="26px"
          letterSpacing="1px"
          _hover={{}}
          _active={{}}
          _focus={{}}
        >
          Get Started
        </Link>
      </Collapse>
    </Box>
  )
}

const X121Menu = ({
  isMobileLandscape = false
}: {
  isMobileLandscape: boolean
}) => {
  const [mobMenuOpen, setMobMenuOpen] = useState(false)

  const onClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault()
    setMobMenuOpen(!mobMenuOpen)
  }
  return (
    <>
      <Button
        // pos="relative"
        // top="1px"
        // right="12px"
        pos="static"
        marginRight="15px"
        ml="auto"
        display={isMobileLandscape ? "flex" : ["flex", "flex", "none"]}
        alignItems="center"
        justifyContent="center"
        minWidth="unset"
        minHeight="unset"
        maxWidth="30.25px"
        width="30.25px"
        maxHeight="32.25px"
        height="32.25px"
        p="8px"
        bg="transparent"
        borderRadius="4px"
        fontSize="16px"
        border="1px solid #888"
        transition="none"
        onClick={onClick}
        _focus={{}}
        _hover={{
          bg: "rgba(0, 0, 0, 0.25)"
        }}
        _active={{
          bg: "rgba(0, 0, 0, 0.25)"
        }}
        marginTop="8px"
        marginBottom="8px"
      >
        <FaBars color="yellow" size={14} />
      </Button>
      <MobMenu isOpen={mobMenuOpen} />
      <Box
        pos="relative"
        right="8px"
        display={isMobileLandscape ? "none" : ["none", "none", "flex"]}
        alignItems="center"
        gap="18px"
      >
        <Link
          href="https://x121.ai/FAQs"
          color="white"
          fontFamily="Roboto"
          fontWeight={400}
          fontSize="16px"
          _active={{}}
          _hover={{}}
          _focus={{}}
        >
          FAQs
        </Link>
        <Link
          href="#"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgColor="#FFC0CB"
          fontWeight="700"
          fontSize="14px"
          fontFamily="Roboto"
          // height="51px"
          p="16px 11px 16px 11px"
          mr="1px"
          borderRadius="5px"
          letterSpacing="1px"
          _hover={{}}
          _active={{}}
          _focus={{}}
        >
          Get Started
        </Link>
      </Box>
    </>
  )
}

export const X121Overlays = ({
  isMobileLandscape,
  isApp,
  footerPos,
  children
}: {
  isMobileLandscape: boolean
  isApp: boolean
  children?: ReactNode
  footerPos?: "relative" | "absolute" | "fixed"
}) => {
  const { isSecureInAppBrowser } = useSearchParams<{
    isSecureInAppBrowser?: true | undefined
  }>()
  return (
    <>
      {!isApp && !isSecureInAppBrowser && (
        <Box
          pos="fixed"
          display="flex"
          flexDir="row"
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          // h="54px"
          top={0}
          bg={isMobileLandscape ? "black" : ["black", "black", "transparent"]}
          paddingTop={[0, 0, "12px"]}
        >
          <ConditionalAppLogo />
          {children ?? <X121Menu isMobileLandscape={isMobileLandscape} />}
        </Box>
      )}
      {!isApp && !isSecureInAppBrowser && <X121Footer position={footerPos} />}
    </>
  )
}

const X121AuthScreen = ({
  children,
  isMobileLandscape,
  isApp,
  roomStyles,
  hideBackButton = true
}: {
  children: ReactNode
  isMobileLandscape: boolean
  isApp: boolean
  roomStyles: AuthPageStyling
  hideBackButton?: boolean
}) => {
  const X121_HEADER_HEIGHT = 50
  const X121_FOOTER_HEIGHT = 37
  const top = `${(X121_HEADER_HEIGHT - X121_FOOTER_HEIGHT) / 2}px`

  if (isMobileLandscape) {
    return (
      <>
        <DefaultLandscapeFormWrap
          roomStyles={roomStyles}
          pt={X121_HEADER_HEIGHT}
          pb={X121_FOOTER_HEIGHT}
        >
          {children}
        </DefaultLandscapeFormWrap>
        <X121Overlays
          isMobileLandscape={isMobileLandscape}
          isApp={isApp}
          footerPos="fixed"
        />
      </>
    )
  }

  return (
    <Flex align="center" justify="center" w="100%" h="100%">
      <DefaultFormWrap
        isMobileLandscape={isMobileLandscape}
        roomStyles={roomStyles}
        top={top}
      >
        {children}
      </DefaultFormWrap>
      <X121Overlays
        isMobileLandscape={isMobileLandscape}
        isApp={isApp}
        footerPos="fixed"
      />
    </Flex>
  )
}

export default X121AuthScreen
